/**
 * Cart Service - Manages shopping cart operations with Firestore
 * Following international e-commerce standards:
 * - Cart stored as subcollection: /users/{userId}/cart/{itemId}
 * - Anonymous users use localStorage, synced to Firestore on login
 * - Real-time cart synchronization across devices
 */

import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  deleteDoc, 
  updateDoc,
  Timestamp,
  query,
  writeBatch
} from 'firebase/firestore';
import { app } from '../firebase/config';
import analyticsService from './analyticsService';

const db = getFirestore(app);
const LOCAL_STORAGE_KEY = 'petzify_cart';

/**
 * Get cart reference for a user
 * @param {string} userId - User email or ID
 * @returns {CollectionReference} - Firestore collection reference
 */
const getUserCartRef = (userId) => {
  return collection(db, 'users', userId, 'cart');
};

/**
 * Get cart item reference
 * @param {string} userId - User email or ID
 * @param {string} itemId - Cart item ID
 * @returns {DocumentReference} - Firestore document reference
 */
const getCartItemRef = (userId, itemId) => {
  return doc(db, 'users', userId, 'cart', itemId);
};

/**
 * Fetch cart from localStorage (for anonymous users)
 * @returns {Array} - Cart items array
 */
export const getLocalCart = () => {
  try {
    const cart = localStorage.getItem(LOCAL_STORAGE_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error reading local cart:', error);
    return [];
  }
};

/**
 * Save cart to localStorage
 * @param {Array} cartItems - Cart items array
 */
export const saveLocalCart = (cartItems) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cartItems));
    // Dispatch event for cart updates
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error saving local cart:', error);
  }
};

/**
 * Clear localStorage cart
 */
export const clearLocalCart = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    // Also remove old 'cart' key if exists (for migration)
    localStorage.removeItem('cart');
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error clearing local cart:', error);
  }
};

/**
 * Fetch cart from Firestore for authenticated user
 * @param {string} userId - User email or ID
 * @returns {Promise<Array>} - Cart items array
 */
export const fetchCart = async (userId) => {
  if (!userId) {
    return getLocalCart();
  }

  try {
    const cartRef = getUserCartRef(userId);
    const cartSnapshot = await getDocs(query(cartRef));
    
    const cartItems = [];
    cartSnapshot.forEach((doc) => {
      const data = doc.data();
      cartItems.push({
        id: doc.id,
        ...data,
        // Convert Timestamp to Date if needed
        addedAt: data.addedAt?.toDate?.() || new Date()
      });
    });

    // Sort by addedAt date (newest first)
    cartItems.sort((a, b) => b.addedAt - a.addedAt);
    
    return cartItems;
  } catch (error) {
    console.error('Error fetching cart from Firestore:', error);
    // Fallback to local cart on error
    return getLocalCart();
  }
};

/**
 * Add item to cart
 * @param {string} userId - User email or ID (null for anonymous)
 * @param {Object} item - Cart item object
 * @returns {Promise<void>}
 */
export const addToCart = async (userId, item) => {
  const cartItem = {
    id: item.id,
    productId: item.productId || item.id,
    name: item.name,
    price: item.price, // This is the sale price
    mrp: item.mrp || item.price, // MRP (original price), fallback to price if not provided
    image: item.image || null,
    quantity: item.quantity || 1,
    addedAt: new Date(),
    // Optional: variant information
    ...(item.variantId && {
      variantId: item.variantId,
      variantTitle: item.variantTitle,
      variantWeight: item.variantWeight
    })
  };

  // For anonymous users, use localStorage
  if (!userId) {
    const localCart = getLocalCart();
    const existingIndex = localCart.findIndex(i => i.id === cartItem.id);
    
    if (existingIndex >= 0) {
      localCart[existingIndex].quantity += cartItem.quantity;
    } else {
      localCart.push(cartItem);
    }
    
    saveLocalCart(localCart);
    
    // Track add to cart event for anonymous users
    analyticsService.logAddToCart(
      cartItem.productId,
      cartItem.name,
      'product',
      cartItem.price,
      cartItem.quantity
    );
    return;
  }

  // For authenticated users, use Firestore
  try {
    const itemRef = getCartItemRef(userId, cartItem.id);
    const itemDoc = await getDoc(itemRef);
    
    if (itemDoc.exists()) {
      // Update quantity if item already exists
      const currentData = itemDoc.data();
      await updateDoc(itemRef, {
        quantity: currentData.quantity + cartItem.quantity,
        updatedAt: Timestamp.now()
      });
    } else {
      // Add new item
      await setDoc(itemRef, {
        ...cartItem,
        addedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
    
    // Track add to cart event for authenticated users
    analyticsService.logAddToCart(
      cartItem.productId,
      cartItem.name,
      'product',
      cartItem.price,
      cartItem.quantity
    );
    
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

/**
 * Update cart item quantity
 * @param {string} userId - User email or ID (null for anonymous)
 * @param {string} itemId - Cart item ID
 * @param {number} quantity - New quantity
 * @returns {Promise<void>}
 */
export const updateCartItemQuantity = async (userId, itemId, quantity) => {
  if (quantity < 1) {
    return removeFromCart(userId, itemId);
  }

  // For anonymous users, use localStorage
  if (!userId) {
    const localCart = getLocalCart();
    const itemIndex = localCart.findIndex(i => i.id === itemId);
    
    if (itemIndex >= 0) {
      localCart[itemIndex].quantity = quantity;
      saveLocalCart(localCart);
    }
    return;
  }

  // For authenticated users, use Firestore
  try {
    const itemRef = getCartItemRef(userId, itemId);
    await updateDoc(itemRef, {
      quantity: quantity,
      updatedAt: Timestamp.now()
    });
    
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error updating cart item:', error);
    throw error;
  }
};

/**
 * Remove item from cart
 * @param {string} userId - User email or ID (null for anonymous)
 * @param {string} itemId - Cart item ID
 * @returns {Promise<void>}
 */
export const removeFromCart = async (userId, itemId) => {
  // For anonymous users, use localStorage
  if (!userId) {
    const localCart = getLocalCart();
    const itemToRemove = localCart.find(i => i.id === itemId);
    const updatedCart = localCart.filter(i => i.id !== itemId);
    saveLocalCart(updatedCart);
    
    // Track remove from cart event for anonymous users
    if (itemToRemove) {
      analyticsService.logRemoveFromCart(
        itemToRemove.productId,
        itemToRemove.name,
        'product',
        itemToRemove.price,
        itemToRemove.quantity
      );
    }
    return;
  }

  // For authenticated users, use Firestore
  try {
    const itemRef = getCartItemRef(userId, itemId);
    const itemDoc = await getDoc(itemRef);
    
    // Track remove from cart event for authenticated users
    if (itemDoc.exists()) {
      const itemData = itemDoc.data();
      analyticsService.logRemoveFromCart(
        itemData.productId,
        itemData.name,
        'product',
        itemData.price,
        itemData.quantity
      );
    }
    
    await deleteDoc(itemRef);
    
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error removing from cart:', error);
    throw error;
  }
};

/**
 * Clear entire cart
 * @param {string} userId - User email or ID (null for anonymous)
 * @returns {Promise<void>}
 */
export const clearCart = async (userId) => {
  // For anonymous users, use localStorage
  if (!userId) {
    clearLocalCart();
    return;
  }

  // For authenticated users, use Firestore
  try {
    const cartRef = getUserCartRef(userId);
    const cartSnapshot = await getDocs(query(cartRef));
    
    // Use batch delete for better performance
    const batch = writeBatch(db);
    cartSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error clearing cart:', error);
    throw error;
  }
};

/**
 * Sync local cart to Firestore when user logs in
 * This is a critical function for merging anonymous cart with user cart
 * @param {string} userId - User email or ID
 * @returns {Promise<void>}
 */
export const syncLocalCartToFirestore = async (userId) => {
  if (!userId) return;

  try {
    const localCart = getLocalCart();
    
    // Check for old 'cart' key and migrate
    const oldCart = localStorage.getItem('cart');
    if (oldCart) {
      try {
        const oldCartData = JSON.parse(oldCart);
        if (Array.isArray(oldCartData) && oldCartData.length > 0) {
          // Merge old cart with current local cart
          oldCartData.forEach(oldItem => {
            const existingIndex = localCart.findIndex(i => i.id === oldItem.id);
            if (existingIndex >= 0) {
              localCart[existingIndex].quantity += oldItem.quantity;
            } else {
              localCart.push(oldItem);
            }
          });
          // Remove old key
          localStorage.removeItem('cart');
        }
      } catch (e) {
        console.error('Error migrating old cart:', e);
      }
    }
    
    if (localCart.length === 0) return;

    // Fetch existing Firestore cart
    const firestoreCart = await fetchCart(userId);
    
    // Merge local cart with Firestore cart
    const batch = writeBatch(db);
    
    for (const localItem of localCart) {
      const existingItem = firestoreCart.find(i => i.id === localItem.id);
      const itemRef = getCartItemRef(userId, localItem.id);
      
      if (existingItem) {
        // Update quantity (add local quantity to existing)
        batch.update(itemRef, {
          quantity: existingItem.quantity + localItem.quantity,
          updatedAt: Timestamp.now()
        });
      } else {
        // Add new item
        batch.set(itemRef, {
          ...localItem,
          addedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }
    }
    
    await batch.commit();
    
    // Clear local cart after successful sync
    clearLocalCart();
    
    console.log('Cart synced successfully from localStorage to Firestore');
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (error) {
    console.error('Error syncing cart to Firestore:', error);
    // Don't clear local cart on error - keep it as backup
  }
};

/**
 * Get cart item count
 * @param {string} userId - User email or ID (null for anonymous)
 * @returns {Promise<number>} - Total item count
 */
export const getCartItemCount = async (userId) => {
  let cartItems;
  
  if (!userId) {
    cartItems = getLocalCart();
  } else {
    try {
      cartItems = await fetchCart(userId);
    } catch (error) {
      console.error('Error getting cart count:', error);
      cartItems = getLocalCart();
    }
  }
  
  return cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
};

/**
 * Calculate cart subtotal
 * @param {Array} cartItems - Cart items array
 * @returns {number} - Subtotal amount
 */
export const calculateSubtotal = (cartItems) => {
  return cartItems.reduce((sum, item) => {
    return sum + ((item.price || 0) * (item.quantity || 0));
  }, 0);
};

export default {
  getLocalCart,
  saveLocalCart,
  clearLocalCart,
  fetchCart,
  addToCart,
  updateCartItemQuantity,
  removeFromCart,
  clearCart,
  syncLocalCartToFirestore,
  getCartItemCount,
  calculateSubtotal
};






